import { generateAutomationName } from "app/pages/activation/helpers/generateAutomationName";
import {
    ACTIVATION_PROJECT_ROUTE,
    DEFAULT_ACTIVATION_GRANULARITY,
    DEFAULT_ACTIVATION_WEB_SOURCE,
} from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getTrafficWidgetState } from "../../utils";
import { EmptyStateOverlayContent } from "../EmptyStateOverlayContent";
import { TrafficWidgetLayout } from "../TrafficWidgetLayout";
import type { IMyTrafficShareProps } from "../types";

import Default from "./BodyByState/Default";
import Empty from "./BodyByState/Empty";
import { Loading } from "./BodyByState/Loading";
import { EMPTY } from "./BodyByState/texts";
import { FOOTER_EMPTY, TITLE, TOOLTIP } from "./texts";

export const MyTrafficShare = ({
    trafficData,
    isLoading,
    isLocked,
    isEmpty,
    onCreateProject,
}: IMyTrafficShareProps) => {
    const state = getTrafficWidgetState({ isEmpty, isLoading, isLocked });

    const trackingApi = activationTracking.homePageWithData;

    const onRedirectToProject = async () => {
        if (!trafficData?.id) {
            return;
        }

        await proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
            key: trafficData?.id,
            country: trafficData?.country,
            webSource: DEFAULT_ACTIVATION_WEB_SOURCE,
            timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
        });
    };

    const onClick = async (event?: React.MouseEvent) => {
        event?.preventDefault();

        if (state === "default") {
            trackingApi.trackClickTrafficShare("view project");
            await onRedirectToProject();
        }

        if (state === "empty") {
            trackingApi.trackClickTrafficShare("create a new project");
            onCreateProject();
        }
    };

    return (
        <TrafficWidgetLayout
            titleKey={TITLE}
            onClick={trafficData?.id ? onClick : undefined}
            tooltipKey={TOOLTIP}
            state={state}
            isLoading={isLoading}
            dataAutomation={generateAutomationName("home", ["share", "data", "my", "traffic", "share"])}
            bodyByState={{
                default: (
                    <Default
                        difference={trafficData?.periodOverPeriodShareDifference}
                        share={trafficData?.share}
                        trendData={trafficData?.shareTrend}
                    />
                ),
                loading: <Loading />,
                empty: <Empty />,
            }}
            emptyStateOverlay={
                state === "empty" ? (
                    <EmptyStateOverlayContent
                        messageKey={EMPTY}
                        ctaKey={FOOTER_EMPTY}
                        onClick={onClick}
                    />
                ) : undefined
            }
        />
    );
};
